// v50.335 — Defence P9: learned match-model client cache.
//
// Serves the "single-line model swap" matchScore.ts has anticipated since
// v50.134. get_active_match_model (migration-148) returns the active
// pair-affinity model — global partner factors plus ONLY the caller's own
// coordinator↔partner affinities — or null when no model has beaten the
// heuristic on holdout yet.
//
// The cache is module-level and primed fire-and-forget; computeBaseScore
// reads it SYNCHRONOUSLY via getLearnedMatchBonus(). Not yet loaded,
// flag off, RPC error, or no active model all resolve to bonus 0 — i.e.
// the deterministic heuristic. The fallback is structural, not a retry.
//
// Direction note: the overlay applies where the viewer scores PARTNER
// companies (pool, shelf, profiles) — pair key `${viewerCo}:${partnerCo}`.
// Requirement-level operative matching gets the same model server-side
// inside rank_operatives_for_requirement, which is the authoritative
// surface and also logs match_predictions.

import { supabase } from "@/lib/api/supabase-client";
import { FLAGS } from "@/lib/flags";

interface ActiveModel {
  model_version: number;
  pairs:         Record<string, number>;
  partners:      Record<string, number>;
}

let viewerCompanyId: string | null = null;
let model: ActiveModel | null = null;
let inflight: Promise<void> | null = null;

/* Fire-and-forget. Idempotent per company; re-priming with a different
   company (account switch) refetches. */
export function primeMatchModel(companyId: string | null | undefined): void {
  if (!FLAGS.mlMatching || !companyId) return;
  if (companyId === viewerCompanyId && (model !== null || inflight !== null)) return;
  viewerCompanyId = companyId;
  model = null;
  inflight = (async () => {
    try {
      const { data, error } = await supabase.rpc("get_active_match_model", { p_company_id: companyId });
      if (error || !data) return;
      const pairs    = (data as Record<string, unknown>).pairs;
      const partners = (data as Record<string, unknown>).partners;
      model = {
        model_version: Number((data as Record<string, unknown>).model_version || 0),
        pairs:    (pairs    && typeof pairs    === "object" ? pairs    : {}) as Record<string, number>,
        partners: (partners && typeof partners === "object" ? partners : {}) as Record<string, number>,
      };
      window.dispatchEvent(new CustomEvent("lc-match-model-loaded"));
    } catch {
      /* heuristic keeps serving */
    } finally {
      inflight = null;
    }
  })();
}

/* Synchronous read for computeBaseScore. Pair points (±6) + global
   partner factor (±3), clamped ±9 as a belt-and-braces serving cap. */
export function getLearnedMatchBonus(partnerCompanyId: string | null | undefined): number {
  if (!FLAGS.mlMatching || !model || !partnerCompanyId || !viewerCompanyId) return 0;
  const pair    = Number(model.pairs[`${viewerCompanyId}:${partnerCompanyId}`] || 0);
  const partner = Number(model.partners[partnerCompanyId] || 0);
  const bonus = (Number.isFinite(pair) ? pair : 0) + (Number.isFinite(partner) ? partner : 0);
  return Math.max(-9, Math.min(9, bonus));
}

export function getActiveModelVersion(): number {
  return model?.model_version ?? 0;
}
